<template>
  <Dialog v-model:visible="dialogVisible" modal header="Edit Calendar"
    :style="{ width: '40rem' }" :breakpoints="{ '640px': '95vw' }" data-testid="edit-calendar-dialog">
    <form @submit.prevent="handleUpdate" class="space-y-2.5">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-0.5">Start Date</label>
          <Calendar v-model="form.started_at" dateFormat="yy-mm-dd" class="w-full" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-0.5">End Date</label>
          <Calendar v-model="form.ended_at" dateFormat="yy-mm-dd" class="w-full" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-0.5">School Days</label>
          <InputNumber v-model="form.total_school_days" class="w-full" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-0.5">Next Calendar Starts</label>
          <Calendar v-model="form.next_calender_starts_at" dateFormat="yy-mm-dd" class="w-full" />
        </div>
      </div>
      <div class="flex items-center gap-1.5">
        <Checkbox v-model="form.is_active" :binary="true" inputId="edit_is_active" />
        <label for="edit_is_active" class="text-xs text-gray-700 font-medium cursor-pointer select-none">
          Set as active calendar
        </label>
      </div>
      <div class="flex justify-end gap-1.5 pt-1">
        <Button type="button" label="Cancel" size="small" severity="secondary" outlined @click="dialogVisible = false" />
        <Button type="submit" label="Save Changes" size="small" icon="pi pi-check" :loading="isSaving" />
      </div>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';

interface CalendarData {
  id: string;
  started_at: string;
  ended_at: string;
  is_active: boolean;
  total_school_days?: number | null;
  next_calender_starts_at: string | null;
}

const props = defineProps<{ calendar: CalendarData | null }>();
const dialogVisible = defineModel<boolean>('visible', { default: false });
const emit = defineEmits<{ updated: [] }>();

const { updateAcademicCalendar } = useSchoolMutations();
const isSaving = ref(false);

const form = ref({
  started_at: undefined as Date | undefined,
  ended_at: undefined as Date | undefined,
  is_active: false,
  total_school_days: undefined as number | undefined,
  next_calender_starts_at: undefined as Date | undefined,
});

// The parent passes pre-formatted date strings ("—" for empty, or "YYYY-MM-DD"),
// so parse defensively — an unparseable/empty value must become an empty field,
// not an Invalid Date (which the Calendar renders as "NaN-NaN-NaN").
function parseDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

watch(() => props.calendar, (cal) => {
  if (cal) {
    form.value = {
      started_at: parseDate(cal.started_at),
      ended_at: parseDate(cal.ended_at),
      is_active: cal.is_active ?? false,
      total_school_days: cal.total_school_days ?? undefined,
      next_calender_starts_at: parseDate(cal.next_calender_starts_at),
    };
  }
}, { immediate: true });

function formatDateStr(date: Date | string | undefined): string | null {
  if (!date) return null;
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

async function handleUpdate() {
  if (!props.calendar?.id) return;
  isSaving.value = true;
  try {
    await updateAcademicCalendar({
      id: props.calendar.id,
      started_at: formatDateStr(form.value.started_at),
      ended_at: formatDateStr(form.value.ended_at),
      is_active: form.value.is_active,
      total_school_days: form.value.total_school_days ?? null,
      next_calender_starts_at: formatDateStr(form.value.next_calender_starts_at),
    });
    dialogVisible.value = false;
    emit('updated');
  } catch {
    // toast handled in composable
  } finally {
    isSaving.value = false;
  }
}
</script>
